import { useConfigContext, useDrawerContext } from "@WahooFitness/wahoo-offline-mfe";
import { getRunningLegendItems } from "../Shared/legendItems";
import { useCallback, useMemo, useState } from "react";
import { FitnessProfileAlgorithm, WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { t } from "@lingui/macro";
import { useTheme } from "@mui/material";
import HowWeCalculateDrawer from "../HowWeCalculateDrawer";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import useLocalizedRunningFitnessData from "./useLocalizedRunningFitnessData";
import { TrendIcon } from "@WahooFitness/redesignr";
import EstimateDrawer from "../EstimateDrawer";
import EditProfileDrawer from "../EditProfileDrawer";
import AthleteProfileLogo from "../AthleteProfileLogo";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import { useNavigate } from "react-router";
import useLocalStorage from "@/hooks/useLocalStorage";

function useRunningFitnessProfile() {
  const { userFitnessProfilesData, userFitnessProfilesIsLoading, userFitnessProfilesError } =
    useFitnessProfiles();
  const [editProfileDrawerOpen, setEditProfileDrawerOpen] = useState(false);
  const handleEditProfileClick = useCallback(() => setEditProfileDrawerOpen(true), []);
  const handleEditDrawerClose = useCallback(() => setEditProfileDrawerOpen(false), []);

  const { logEvent } = useLogEvent();
  const { data: hasSeenThreeDPEducationFlow } = useLocalStorage("hasSeenThreeDPEducationFlow");

  const { getRunnerTypeById } = useLocalizedRunningFitnessData();
  const { setDrawer } = useDrawerContext();
  const { globalBottomPadding } = useConfigContext();
  const navigate = useNavigate();

  const runningFitnessProfile = useMemo(
    () =>
      userFitnessProfilesData?.find(
        (profile) => profile.workout_type_family_id === WahooWorkoutTypeFamily.Running
      ),
    [userFitnessProfilesData]
  );

  const { getRunnerTypeStrengthById, getRunnerTypeWeaknessById } = useLocalizedRunningFitnessData();

  const athleteStrength = useMemo(
    () => getRunnerTypeStrengthById(runningFitnessProfile?.fitness_strength_id || undefined),
    [runningFitnessProfile?.fitness_strength_id, getRunnerTypeStrengthById]
  );
  const athleteWeakness = useMemo(
    () => getRunnerTypeWeaknessById(runningFitnessProfile?.fitness_weakness_id || undefined),
    [runningFitnessProfile?.fitness_weakness_id, getRunnerTypeWeaknessById]
  );
  const strength = useMemo(() => {
    if (athleteStrength) {
      return {
        name: athleteStrength?.name,
        description: athleteStrength?.description,
        icon: <TrendIcon size="small" direction="up" color="success" />,
      };
    }
  }, [athleteStrength]);
  const weakness = useMemo(() => {
    if (athleteWeakness) {
      return {
        name: athleteWeakness?.name,
        description: athleteWeakness?.description,
        icon: <TrendIcon size="small" direction="down" color="error" />,
      };
    }
  }, [athleteWeakness]);

  const runnerTypeData = useMemo(
    () => getRunnerTypeById(runningFitnessProfile?.fitness_type_id ?? undefined),
    [runningFitnessProfile?.fitness_type_id, getRunnerTypeById]
  );

  const runnerTypeIcon = useMemo(() => runnerTypeData?.icon, [runnerTypeData?.icon]);
  const runnerTypeIconFilled = useMemo(
    () => runnerTypeData?.iconFilled,
    [runnerTypeData?.iconFilled]
  );
  const profileTypeName = runnerTypeData?.name ?? t`Not set`;

  const profileDescription = runnerTypeData?.description;

  const { palette } = useTheme();

  const hasProfile = !!runningFitnessProfile;

  const dimensions = useMemo(() => {
    return {
      tp: {
        value: runningFitnessProfile?.dim_1 ?? 0,
        rank: runningFitnessProfile?.dim_1_rank ?? 0,
      },
      map: {
        value: runningFitnessProfile?.dim_2 ?? 0,
        rank: runningFitnessProfile?.dim_2_rank ?? 0,
      },
      ac: {
        value: runningFitnessProfile?.dim_3 ?? 0,
        rank: runningFitnessProfile?.dim_3_rank ?? 0,
      },
    };
  }, [
    runningFitnessProfile?.dim_1,
    runningFitnessProfile?.dim_1_rank,
    runningFitnessProfile?.dim_2,
    runningFitnessProfile?.dim_2_rank,
    runningFitnessProfile?.dim_3,
    runningFitnessProfile?.dim_3_rank,
  ]);

  const legendItems = getRunningLegendItems(palette);

  const openInfoDrawer = useCallback(() => {
    setDrawer({
      open: true,
      title: t`How we calculate?`,
      hidePuller: true,
      children: (
        <HowWeCalculateDrawer
          infoText={t`Each metric is positioned on a -1 to 1 scale relative to your other capabilities. Your strongest dimensions appear closer to 1 (strength), and your weaker areas closer to -1 (weakness). Metrics near 0 (baseline) align with the average for your demographic.`}
        />
      ),
      globalBottomPadding,
    });
  }, [setDrawer, globalBottomPadding]);

  const isRunningTypeManual = useMemo(
    () => runningFitnessProfile?.fitness_profile_algorithm_id === FitnessProfileAlgorithm.Custom,
    [runningFitnessProfile?.fitness_profile_algorithm_id]
  );

  const isRunningTypeEstimate = useMemo(() => {
    return (
      runningFitnessProfile?.fitness_profile_algorithm_id === FitnessProfileAlgorithm.ThreeDPQuiz
    );
  }, [runningFitnessProfile?.fitness_profile_algorithm_id]);

  const openEstimateExplanationDrawer = useCallback(() => {
    logEvent("athlete_profile_running", {
      click_target: ClickTarget.estimate,
      source_page_name: SourcePage.athlete_profile_running,
    });
    setDrawer({
      open: true,
      hidePuller: true,
      title: isRunningTypeManual ? t`About manual tag` : t`Why is this an estimate?`,
      children: (
        <EstimateDrawer
          fitnessProfileType={WahooWorkoutTypeFamily.Running}
          algorithmId={runningFitnessProfile?.fitness_profile_algorithm_id}
        />
      ),
      globalBottomPadding,
    });
  }, [
    logEvent,
    setDrawer,
    isRunningTypeManual,
    runningFitnessProfile?.fitness_profile_algorithm_id,
    globalBottomPadding,
  ]);

  const handleQuestionnaireClick = useCallback(() => {
    setDrawer({ open: false });
    logEvent("athlete_profile_running", {
      click_target: ClickTarget.start_quiz,
      source_page_name: SourcePage.athlete_profile_running,
    });
    hasSeenThreeDPEducationFlow ? navigate("/threedp?skipEducation") : navigate("/threedp");
  }, [hasSeenThreeDPEducationFlow, logEvent, navigate, setDrawer]);

  const openUpdateProfileDrawer = useCallback(() => {
    logEvent("athlete_profile_running", {
      click_target: ClickTarget.update_profile,
      source_page_name: SourcePage.athlete_profile_running,
    });
    setDrawer({
      open: true,
      title: hasProfile ? t`Update profile` : t`Start profiling`,
      hidePuller: true,
      children: (
        <EditProfileDrawer
          onStartQuestionnaire={handleQuestionnaireClick}
          questionnaireIcon={<AthleteProfileLogo profileType={WahooWorkoutTypeFamily.Running} />}
          showManualUpdateButton={hasProfile}
          onManualUpdateClick={handleEditProfileClick}
        />
      ),
      elevation: 4,
    });
  }, [logEvent, setDrawer, hasProfile, handleEditProfileClick, handleQuestionnaireClick]);

  return {
    runningFitnessProfile,
    editProfileDrawerOpen,
    handleEditDrawerClose,
    strength,
    weakness,
    runningFitnessProfileIsLoading: userFitnessProfilesIsLoading,
    runningFitnessProfileError: userFitnessProfilesError,
    profileTypeName,
    runnerTypeIcon,
    runnerTypeIconFilled,
    profileDescription,
    dimensions,
    legendItems,
    hasProfile,
    openInfoDrawer,
    isRunningTypeEstimate,
    isRunningTypeManual,
    openEstimateExplanationDrawer,
    openUpdateProfileDrawer,
  };
}

export default useRunningFitnessProfile;
